'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import NationalCapacityHeatMap from './NationalCapacityHeatMap';
import AddNewCrewDrawer from './AddNewCrewDrawer';
import Row1ComplianceAndOnboarding from './Row1ComplianceAndOnboarding';
import Row2PerformanceAndJobs from './Row2PerformanceAndJobs';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ComplianceIssue {
  type: string;
  severity: string;
  due_date: string;
}

interface AvailabilityMap {
  Mon: string;
  Tue: string;
  Wed: string;
  Thu: string;
  Fri: string;
  Sat: string;
  Sun: string;
}

interface PerformanceMetrics {
  on_time_arrival: number;
  on_time_completion: number;
  cost_accuracy: number;
}

interface Document {
  name: string;
  upload_date: string | null;
  expiration_date: string | null;
  status: 'valid' | 'expired' | 'pending';
}

interface UpcomingJob {
  job_id: string;
  date: string;
  address: string;
}

interface InstallerCrew {
  id: string;
  company_name: string;
  foreman_name: string;
  num_crews: number;
  crew_size: string;
  customer_rating: number;
  quality_rating: string;
  compliance_status: string;
  compliance_issues: ComplianceIssue[];
  availability: AvailabilityMap;
  performance_metrics: PerformanceMetrics;
  documents: Document[];
  upcoming_jobs: UpcomingJob[];
  onboarding_step: number | null;
  contact_phone: string;
  contact_email: string;
  service_states: string[];
  created_at: string;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const StarRating = ({ rating }: { rating: number }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-0.5">
      {stars.map((s) => (
        <svg key={s} className={`w-3.5 h-3.5 ${s <= Math.round(rating) ? 'text-[#F59E0B]' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

const CircularProgress = ({ value, label, color }: { value: number; label: string; color: string }) => {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative w-16 h-16">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 72 72">
          <circle cx="36" cy="36" r={radius} fill="none" stroke="#F3F4F6" strokeWidth="6" />
          <circle
            cx="36" cy="36" r={radius} fill="none"
            stroke={color} strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#000150]">
          {value > 0 ? `${value}%` : 'N/A'}
        </span>
      </div>
      <span className="text-[10px] text-gray-500 text-center leading-tight max-w-[64px]">{label}</span>
    </div>
  );
};

const OnboardingStepper = ({ step }: { step: number }) => {
  const steps = ['Application', 'Background Check', 'Insurance Verify', 'OBQ Certified'];
  return (
    <div className="w-full">
      <p className="text-xs font-semibold text-[#000150] mb-3">Onboarding Progress</p>
      <div className="flex items-center w-full">
        {steps.map((s, i) => {
          const stepNum = i + 1;
          const isCompleted = stepNum < step;
          const isCurrent = stepNum === step;
          return (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                  isCompleted ? 'bg-[#10B981] border-[#10B981] text-white' :
                  isCurrent ? 'bg-[#07B0E9] border-[#07B0E9] text-white': 'bg-white border-gray-200 text-gray-400'
                }`}>
                  {isCompleted ? <Icon name="CheckIcon" size={12} /> : stepNum}
                </div>
                <span className={`text-[9px] mt-1 text-center leading-tight max-w-[52px] ${isCurrent ? 'text-[#07B0E9] font-semibold' : isCompleted ? 'text-[#10B981]' : 'text-gray-400'}`}>
                  {s}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 mb-4 ${isCompleted ? 'bg-[#10B981]' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const InstallerNetworkInteractive = () => {
  const { currentUser } = useAuth();
  const router = useRouter();
  const [crews, setCrews] = useState<InstallerCrew[]>([]);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState<InstallerCrew | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrawerTab, setActiveDrawerTab] = useState<'overview' | 'documents' | 'schedule'>('overview');
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedStateName, setSelectedStateName] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [addNewCrewOpen, setAddNewCrewOpen] = useState(false);
  const [selectedCrewIds, setSelectedCrewIds] = useState<Set<string>>(new Set());
  const supabase = createClient();

  // Bulk-selection state clears on search filter change so the user returns
  // to a clean list state when the visible set shifts.
  useEffect(() => {
    setSelectedCrewIds(new Set());
  }, [searchQuery, selectedState]);

  const fetchCrews = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('installer_crews')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      setCrews((data as InstallerCrew[]) || []);
    } catch {
      setCrews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setMounted(true);
    fetchCrews();
    const channel = supabase
      .channel('installer_crews_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'installer_crews' }, () => {
        fetchCrews();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchCrews]);

  const handleStateClick = (abbr: string | null, stateName: string) => {
    if (abbr === null || abbr === selectedState) {
      setSelectedState(null);
      setSelectedStateName('');
    } else {
      // Briefly highlight the state on the map then navigate
      setSelectedState(abbr);
      setSelectedStateName(stateName);
      setTimeout(() => {
        router.push(`/state-specific-crew-management-view?state=${abbr}`);
      }, 300);
    }
  };

  const filteredCrews = (() => {
    let result = selectedState
      ? crews.filter((c) => c.service_states?.includes(selectedState))
      : crews;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.company_name.toLowerCase().includes(q) ||
          c.foreman_name.toLowerCase().includes(q) ||
          (c as any).city?.toLowerCase().includes(q)
      );
    }
    return result;
  })();

  const openDrawer = (crew: InstallerCrew) => {
    setSelectedCrew(crew);
    setActiveDrawerTab('overview');
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Clear bulk selection on drawer close — opening a crew puts the user
    // into single-crew mode, so selection is no longer load-bearing.
    setSelectedCrewIds(new Set());
    setTimeout(() => setSelectedCrew(null), 300);
  };

  const toggleCrewSelected = (crewId: string) => {
    setSelectedCrewIds((prev) => {
      const next = new Set(prev);
      if (next.has(crewId)) next.delete(crewId);
      else next.add(crewId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedCrewIds((prev) => {
      if (prev.size === filteredCrews.length) return new Set();
      return new Set(filteredCrews.map((c) => c.id));
    });
  };

  const openDrawerById = (crewId: string) => {
    const crew = crews.find((c) => c.id === crewId);
    if (crew) openDrawer(crew);
  };

  // ─── Metrics ────────────────────────────────────────────────────────────────
  const activeCrews = crews.filter(c => c.compliance_status !== 'onboarding').length;
  const onboardingCrews = crews.filter(c => c.compliance_status === 'onboarding').length;
  const complianceWarnings = crews.filter(c => c.compliance_status === 'action_needed').length;
  const totalCapacity = crews
    .filter(c => c.compliance_status === 'compliant')
    .reduce((sum, c) => {
      const avgSize = parseInt(c.crew_size.split('-')[0]) || 4;
      return sum + Math.floor(avgSize / 2);
    }, 0);

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

  const getQualityBadgeColor = (rating: string) => {
    if (rating.startsWith('A')) return 'bg-[#ECFDF5] text-[#10B981] border border-[#10B981]/30';
    if (rating.startsWith('B')) return 'bg-blue-50 text-[#033494] border border-[#033494]/20';
    return 'bg-gray-100 text-gray-500 border border-gray-200';
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8F9FA]">

      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <div className="sticky top-[60px] z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3.5">
          <div>
            <h1 className="text-lg font-bold text-[#000150] leading-tight">Installer Network Management</h1>
            <p className="text-xs text-gray-500 mt-0.5">Operations & Vendor Management · Live Network View</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              suppressHydrationWarning
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-all active:scale-95"
              style={{ background: '#07B0E9' }}
              onClick={() => setAddNewCrewOpen(true)}
            >
              <Icon name="PlusIcon" size={16} />
              Add New Crew
            </button>
            <button suppressHydrationWarning className="relative w-9 h-9 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-all border border-gray-100">
              <Icon name="ChatBubbleLeftEllipsisIcon" size={18} className="text-gray-500" />
            </button>
            <button suppressHydrationWarning className="relative w-9 h-9 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-all border border-gray-100">
              <Icon name="BellIcon" size={18} className="text-gray-500" />
              {mounted && complianceWarnings > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#EF4444] text-white text-[9px] font-bold flex items-center justify-center">
                  {complianceWarnings}
                </span>
              )}
            </button>
            <div suppressHydrationWarning className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-sm" style={{ background: 'linear-gradient(135deg, #07B0E9, #033494)' }}>
              {mounted ? `${currentUser.first_name.charAt(0)}${currentUser.last_name.charAt(0)}` : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-[1400px] mx-auto">

        {/* ── National Capacity Heat Map ─────────────────────────────────── */}
        <NationalCapacityHeatMap selectedState={selectedState} onStateClick={handleStateClick} />

        {/* ── Metric Cards ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Active Crews', value: (!mounted || loading) ? '—' : String(activeCrews), icon: 'WrenchScrewdriverIcon', color: '#07B0E9', bg: '#EFF9FE', sub: 'Fully operational' },
            { label: 'Crews Onboarding', value: (!mounted || loading) ? '—' : String(onboardingCrews), icon: 'UserPlusIcon', color: '#F59E0B', bg: '#FFFBEB', sub: 'In progress' },
            { label: 'Compliance Warnings', value: (!mounted || loading) ? '—' : String(complianceWarnings), icon: 'ExclamationTriangleIcon', color: '#EF4444', bg: '#FEF2F2', sub: 'Expired documents' },
            { label: 'Network Capacity', value: (!mounted || loading) ? '—' : `${totalCapacity} Roofs/Day`, icon: 'ChartBarIcon', color: '#10B981', bg: '#ECFDF5', sub: 'Compliant crews only' },
          ].map((m) => (
            <div key={m.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: m.bg }}>
                <Icon name={m.icon as any} size={20} style={{ color: m.color }} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 font-medium truncate">{m.label}</p>
                <p className="text-xl font-bold text-[#000150] leading-tight mt-0.5">{m.value}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{m.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── New Tier-1 widget rows (between KPI strip and Crew Directory) ── */}
        <Row1ComplianceAndOnboarding crews={crews} onOpenCrew={openDrawerById} />
        <Row2PerformanceAndJobs crews={crews} />

        {/* ── Crew Directory ────────────────────────────────────────────────── */}
        <div className="mb-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Search Input */}
            <div className="relative w-72 flex-shrink-0">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by company, foreman, or city…"
                suppressHydrationWarning
                className="w-full pl-9 pr-8 py-2 text-xs rounded-xl border border-gray-200 bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  <svg className="w-2.5 h-2.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <h2 className="text-sm font-bold text-[#000150] whitespace-nowrap">Installer Crew Directory</h2>
            {/* Select-all checkbox — checked when all visible crews are selected */}
            <label className="flex items-center gap-1.5 ml-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filteredCrews.length > 0 && selectedCrewIds.size === filteredCrews.length}
                onChange={toggleSelectAll}
                suppressHydrationWarning
                className="w-3.5 h-3.5 rounded border-gray-300 text-[#07B0E9] focus:ring-[#07B0E9]/30"
              />
              <span className="text-[11px] font-semibold text-gray-500">Select all</span>
            </label>
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">{filteredCrews.length} of {crews.length} crews</span>
        </div>

        {/* ── State Filter Banner ───────────────────────────────────────────── */}
        {selectedState && (
          <div className="flex items-center justify-between bg-[#EFF9FE] border border-[#07B0E9]/30 rounded-xl px-4 py-2.5 mb-3">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-[#07B0E9] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-semibold text-[#033494]">
                Showing crews in: <span className="text-[#07B0E9]">{selectedStateName}</span>
              </span>
              <span className="text-xs text-gray-500">({filteredCrews.length} crew{filteredCrews.length !== 1 ? 's' : ''} found)</span>
            </div>
            <button
              onClick={() => handleStateClick(null, '')}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-[#EF4444] transition-colors"
            >
              Clear filter
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 rounded-full border-[3px] border-[#07B0E9] border-t-transparent animate-spin" />
          </div>
        ) : filteredCrews.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-dashed border-gray-200">
            <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <p className="text-sm font-semibold text-gray-500">No crews operating in {selectedStateName}</p>
            <p className="text-xs text-gray-400 mt-1">Try selecting a different state or clear the filter to see all crews</p>
            <button
              onClick={() => handleStateClick(null, '')}
              className="mt-4 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90"
              style={{ background: '#07B0E9' }}
            >
              Show All Crews
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCrews.map((crew) => (
              <div
                key={crew.id}
                onClick={() => openDrawer(crew)}
                className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-[#07B0E9]/30 transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-4 p-4">

                  {/* Bulk-selection checkbox — stops propagation so clicking
                      the checkbox doesn't also open the crew detail drawer. */}
                  <label
                    className="flex items-center cursor-pointer flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCrewIds.has(crew.id)}
                      onChange={() => toggleCrewSelected(crew.id)}
                      suppressHydrationWarning
                      className="w-4 h-4 rounded border-gray-300 text-[#07B0E9] focus:ring-[#07B0E9]/30"
                    />
                  </label>

                  {/* Company Avatar */}
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-base flex-shrink-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #033494, #07B0E9)' }}>
                    {crew.company_name.charAt(0)}
                  </div>

                  {/* Basic Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#000150] group-hover:text-[#07B0E9] transition-colors">{crew.company_name}</h3>
                      {crew.compliance_status === 'onboarding' && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FFFBEB] text-[#F59E0B] border border-[#F59E0B]/30">
                          Onboarding · Step {crew.onboarding_step}/4
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Foreman: <span className="font-medium text-gray-700">{crew.foreman_name}</span></p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] text-gray-400">
                        <span className="font-medium text-gray-600">{crew.num_crews}</span> crews
                      </span>
                      <span className="text-gray-200">·</span>
                      <span className="text-[10px] text-gray-400">
                        <span className="font-medium text-gray-600">{crew.crew_size}</span>
                      </span>
                    </div>
                  </div>

                  {/* Ratings */}
                  <div className="hidden sm:flex flex-col items-center gap-1.5 flex-shrink-0 w-24">
                    {crew.customer_rating > 0 ? (
                      <>
                        <StarRating rating={crew.customer_rating} />
                        <span className="text-[10px] text-gray-500">{crew.customer_rating}/5 Customer</span>
                      </>
                    ) : (
                      <span className="text-[10px] text-gray-400 text-center">No ratings yet</span>
                    )}
                    {crew.quality_rating !== 'N/A' && (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${getQualityBadgeColor(crew.quality_rating)}`}>
                        {crew.quality_rating}
                      </span>
                    )}
                  </div>

                  {/* Compliance Pill */}
                  <div className="hidden md:flex flex-col items-center flex-shrink-0 w-36">
                    {crew.compliance_status === 'compliant' ? (
                      <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#ECFDF5] text-[#10B981] border border-[#10B981]/30">
                        <Icon name="CheckCircleIcon" size={13} />
                        100% Compliant
                      </span>
                    ) : crew.compliance_status === 'action_needed' ? (
                      <div className="text-center">
                        <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#FEF2F2] text-[#EF4444] border border-[#EF4444]/30">
                          <Icon name="ExclamationCircleIcon" size={13} />
                          Action Needed
                        </span>
                        {crew.compliance_issues[0] && (
                          <p className="text-[9px] text-[#EF4444] mt-1 leading-tight">{crew.compliance_issues[0].type}</p>
                        )}
                      </div>
                    ) : (
                      <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#FFFBEB] text-[#F59E0B] border border-[#F59E0B]/30">
                        <Icon name="ClockIcon" size={13} />
                        Onboarding
                      </span>
                    )}
                  </div>

                  {/* Availability Grid */}
                  <div className="hidden lg:flex flex-col items-center flex-shrink-0">
                    <p className="text-[9px] text-gray-400 mb-1.5 font-medium uppercase tracking-wide">Availability</p>
                    <div className="flex items-center gap-1">
                      {DAYS.map((day) => {
                        const status = crew.availability?.[day] || 'off';
                        return (
                          <div key={day} className="flex flex-col items-center gap-0.5">
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[8px] font-bold transition-all ${
                              status === 'available' ? 'bg-[#10B981] text-white' :
                              status === 'booked'? 'bg-gray-200 text-gray-400' : 'bg-gray-100 text-gray-300'
                            }`}>
                              {day.charAt(0)}
                            </div>
                            <span className="text-[7px] text-gray-300">{day.slice(1)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex-shrink-0 ml-2">
                    <Icon name="ChevronRightIcon" size={16} className="text-gray-300 group-hover:text-[#07B0E9] transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Slide-out Drawer Overlay ─────────────────────────────────────────
          Slides from below the app top bar (60px). z-30 sits below the top
          bar's z-header so the top bar stays sovereign no matter how
          stacking is reshuffled in the future. */}
      {drawerOpen && (
        <div className="fixed top-[60px] left-0 right-0 bottom-0 z-30 flex justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeDrawer}
          />

          {/* Drawer Panel */}
          <div
            className={`relative w-full max-w-[580px] h-full bg-white shadow-2xl flex flex-col overflow-hidden transition-transform duration-300 ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
          >
            {selectedCrew && (
              <>
                {/* Drawer Header */}
                <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #000150 0%, #033494 100%)' }}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white font-bold text-lg">
                        {selectedCrew.company_name.charAt(0)}
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-white">{selectedCrew.company_name}</h2>
                        <p className="text-xs text-white/70 mt-0.5">Foreman: {selectedCrew.foreman_name}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="flex items-center gap-1 text-[10px] text-white/60">
                            <Icon name="PhoneIcon" size={10} />
                            {selectedCrew.contact_phone}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] text-white/60">
                            <Icon name="EnvelopeIcon" size={10} />
                            {selectedCrew.contact_email}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-1.5 rounded-lg border border-white/30 text-white text-xs font-semibold hover:bg-white/10 transition-all">
                        Edit Profile
                      </button>
                      <button onClick={closeDrawer} className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all">
                        <Icon name="XMarkIcon" size={16} className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Onboarding Stepper (if applicable) */}
                {selectedCrew.onboarding_step !== null && (
                  <div className="flex-shrink-0 px-6 py-4 bg-[#FFFBEB] border-b border-[#F59E0B]/20">
                    <OnboardingStepper step={selectedCrew.onboarding_step} />
                  </div>
                )}

                {/* Drawer Tabs */}
                <div className="flex-shrink-0 flex border-b border-gray-100 bg-gray-50/50">
                  {(['overview', 'documents', 'schedule'] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveDrawerTab(tab)}
                      className={`flex-1 py-3 text-xs font-semibold capitalize transition-all border-b-2 ${
                        activeDrawerTab === tab
                          ? 'border-[#07B0E9] text-[#07B0E9] bg-white'
                          : 'border-transparent text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      {tab === 'overview' ? 'Performance' : tab === 'documents' ? 'Compliance & Docs' : 'Schedule'}
                    </button>
                  ))}
                </div>

                {/* Drawer Content */}
                <div className="flex-1 overflow-y-auto px-6 py-5">

                  {/* ── Performance Tab ──────────────────────────────────── */}
                  {activeDrawerTab === 'overview' && (
                    <div className="space-y-5">
                      {/* Performance Metrics */}
                      <div>
                        <h3 className="text-xs font-bold text-[#000150] uppercase tracking-wide mb-4">Performance Metrics</h3>
                        {selectedCrew.compliance_status === 'onboarding' ? (
                          <div className="flex items-center justify-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <div className="text-center">
                              <Icon name="ChartBarIcon" size={28} className="text-gray-300 mx-auto mb-2" />
                              <p className="text-xs text-gray-400">No performance data yet</p>
                              <p className="text-[10px] text-gray-300 mt-1">Available after first completed job</p>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-xl p-5">
                            <CircularProgress
                              value={selectedCrew.performance_metrics?.on_time_arrival || 0}
                              label="On-Time Arrival"
                              color="#07B0E9"
                            />
                            <CircularProgress
                              value={selectedCrew.performance_metrics?.on_time_completion || 0}
                              label="On-Time Completion"
                              color="#033494"
                            />
                            <CircularProgress
                              value={selectedCrew.performance_metrics?.cost_accuracy || 0}
                              label="At Quoted Cost"
                              color="#10B981"
                            />
                          </div>
                        )}
                      </div>

                      {/* Crew Details */}
                      <div>
                        <h3 className="text-xs font-bold text-[#000150] uppercase tracking-wide mb-3">Crew Details</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { label: 'Number of Crews', value: String(selectedCrew.num_crews) },
                            { label: 'Crew Size', value: selectedCrew.crew_size },
                            { label: 'Customer Rating', value: selectedCrew.customer_rating > 0 ? `${selectedCrew.customer_rating}/5` : 'No data' },
                            { label: 'Quality Rating', value: selectedCrew.quality_rating },
                          ].map((item) => (
                            <div key={item.label} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                              <p className="text-[10px] text-gray-400 font-medium">{item.label}</p>
                              <p className="text-sm font-bold text-[#000150] mt-0.5">{item.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Availability */}
                      <div>
                        <h3 className="text-xs font-bold text-[#000150] uppercase tracking-wide mb-3">This Week's Availability</h3>
                        <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-4">
                          {DAYS.map((day) => {
                            const status = selectedCrew.availability?.[day] || 'off';
                            return (
                              <div key={day} className="flex-1 flex flex-col items-center gap-1.5">
                                <div className={`w-full aspect-square max-w-[40px] rounded-lg flex items-center justify-center text-xs font-bold ${
                                  status === 'available' ? 'bg-[#10B981] text-white' :
                                  status === 'booked' ? 'bg-gray-200 text-gray-400' : 'bg-gray-100 text-gray-300'
                                }`}>
                                  {day.charAt(0)}
                                </div>
                                <span className="text-[9px] text-gray-400">{day}</span>
                                <span className={`text-[8px] font-medium capitalize ${
                                  status === 'available' ? 'text-[#10B981]' :
                                  status === 'booked' ? 'text-gray-400' : 'text-gray-300'
                                }`}>{status}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-4 mt-2 px-1">
                          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-[#10B981]" /><span className="text-[10px] text-gray-500">Available</span></div>
                          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-gray-200" /><span className="text-[10px] text-gray-500">Booked</span></div>
                          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-gray-100" /><span className="text-[10px] text-gray-500">Off</span></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Documents Tab ─────────────────────────────────────── */}
                  {activeDrawerTab === 'documents' && (
                    <div className="space-y-3">
                      <h3 className="text-xs font-bold text-[#000150] uppercase tracking-wide mb-4">Compliance Documents</h3>
                      {selectedCrew.documents?.map((doc, i) => (
                        <div key={i} className={`flex items-center justify-between p-4 rounded-xl border ${
                          doc.status === 'expired' ? 'bg-[#FEF2F2] border-[#EF4444]/20' :
                          doc.status === 'pending' ? 'bg-[#FFFBEB] border-[#F59E0B]/20' :
                          'bg-[#ECFDF5] border-[#10B981]/20'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              doc.status === 'valid' ? 'bg-[#10B981]/10' :
                              doc.status === 'expired' ? 'bg-[#EF4444]/10' :
                              'bg-[#F59E0B]/10'
                            }`}>
                              {doc.status === 'valid' ? (
                                <Icon name="CheckCircleIcon" size={16} className="text-[#10B981]" />
                              ) : doc.status === 'expired' ? (
                                <Icon name="XCircleIcon" size={16} className="text-[#EF4444]" />
                              ) : (
                                <Icon name="ClockIcon" size={16} className="text-[#F59E0B]" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-[#000150]">{doc.name}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                {doc.upload_date && (
                                  <span className="text-[10px] text-gray-500">Uploaded: {doc.upload_date}</span>
                                )}
                                {doc.expiration_date && (
                                  <span className={`text-[10px] font-medium ${doc.status === 'expired' ? 'text-[#EF4444]' : 'text-gray-500'}`}>
                                    Exp: {doc.expiration_date}
                                  </span>
                                )}
                                {!doc.upload_date && <span className="text-[10px] text-[#F59E0B]">Not yet uploaded</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-full capitalize ${
                              doc.status === 'valid' ? 'bg-[#10B981]/10 text-[#10B981]' :
                              doc.status === 'expired' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                              'bg-[#F59E0B]/10 text-[#F59E0B]'
                            }`}>
                              {doc.status}
                            </span>
                            {(doc.status === 'expired' || doc.status === 'pending') && (
                              <button className="text-[10px] font-semibold text-[#07B0E9] hover:underline flex-shrink-0">Upload</button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Compliance Issues */}
                      {selectedCrew.compliance_issues?.length > 0 && (
                        <div className="mt-4 p-4 bg-[#FEF2F2] rounded-xl border border-[#EF4444]/20">
                          <p className="text-xs font-bold text-[#EF4444] mb-2 flex items-center gap-1.5">
                            <Icon name="ExclamationTriangleIcon" size={13} />
                            Action Required
                          </p>
                          {selectedCrew.compliance_issues.map((issue, i) => (
                            <p key={i} className="text-xs text-[#EF4444]/80">{issue.type} — Due: {issue.due_date}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Schedule Tab ──────────────────────────────────────── */}
                  {activeDrawerTab === 'schedule' && (
                    <div className="space-y-3">
                      <h3 className="text-xs font-bold text-[#000150] uppercase tracking-wide mb-4">Upcoming Assignments (Next 2 Weeks)</h3>
                      {selectedCrew.upcoming_jobs?.length === 0 ? (
                        <div className="flex items-center justify-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                          <div className="text-center">
                            <Icon name="CalendarDaysIcon" size={28} className="text-gray-300 mx-auto mb-2" />
                            <p className="text-xs text-gray-400">No upcoming jobs assigned</p>
                            <p className="text-[10px] text-gray-300 mt-1">This crew is available for scheduling</p>
                          </div>
                        </div>
                      ) : (
                        selectedCrew.upcoming_jobs.map((job, i) => (
                          <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-[#07B0E9]/30 transition-all">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #EFF9FE, #E0F2FE)' }}>
                              <Icon name="HomeIcon" size={18} className="text-[#07B0E9]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-[#033494]">{job.job_id}</span>
                                <span className="text-[10px] text-gray-400">{job.date}</span>
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5 truncate">{job.address}</p>
                            </div>
                            <button className="text-[10px] font-semibold text-[#07B0E9] hover:underline flex-shrink-0">View Job</button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Floating Bulk Action Bar ──────────────────────────────────────
          Renders at viewport bottom when 1+ crews are selected. z-30 sits
          below the top bar's z-header (same defensive choice as the crew
          detail drawer). All actions are no-ops today — wiring captured
          in commit footer backlog. */}
      {selectedCrewIds.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 rounded-xl px-4 py-2.5 flex items-center gap-3 shadow-2xl"
          style={{ background: '#000150', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <span className="text-xs font-bold text-white">
            {selectedCrewIds.size} selected
          </span>
          <span className="text-white/30 text-xs">·</span>
          <button
            onClick={() => console.log('[InstallerNetwork] Bulk: Send compliance reminder (no-op)', Array.from(selectedCrewIds))}
            className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
            style={{ background: 'rgba(7,176,233,0.85)' }}
            suppressHydrationWarning
          >
            Send compliance reminder
          </button>
          <button
            onClick={() => console.log('[InstallerNetwork] Bulk: Export CSV (no-op)', Array.from(selectedCrewIds))}
            className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
            style={{ background: 'rgba(255,255,255,0.1)' }}
            suppressHydrationWarning
          >
            Export CSV
          </button>
          <button
            onClick={() => console.log('[InstallerNetwork] Bulk: Bulk message (no-op)', Array.from(selectedCrewIds))}
            className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
            style={{ background: 'rgba(255,255,255,0.1)' }}
            suppressHydrationWarning
          >
            Bulk message
          </button>
          <button
            onClick={() => setSelectedCrewIds(new Set())}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
            suppressHydrationWarning
            title="Clear selection"
          >
            <Icon name="XMarkIcon" size={14} />
          </button>
        </div>
      )}

      {/* ── Add New Crew drawer (slides from right, below top bar) ────────── */}
      <AddNewCrewDrawer
        open={addNewCrewOpen}
        onClose={() => setAddNewCrewOpen(false)}
      />
    </div>
  );
};

export default InstallerNetworkInteractive;
